import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Donation.css';

import { FaDollarSign, FaAngleDown } from 'react-icons/fa';
import axios from "axios";
import OfflineInstructions from '../../../components/OfflineInstructions/OfflineInstructions';

const API_PATH = 'https://blazeguard-react.asdesignsgalaxy.com/api/phpmailer/donation.php';

const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;



const Donation = () => {

  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    campaignType: '',
    amount: '',
    payment: 'offline',
    name: '',
    email: '',
    number: '',
    address: '',
    comment: ''
  });

  const [selectedAmount, setSelectedAmount] = useState(null);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAmountSelection = (amount) => {
    setSelectedAmount(amount);
    setFormData(prev => ({ ...prev, amount: amount.toString() }));
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const validationErrors = [];
      
      if (!formData.campaignType) validationErrors.push('Campaign selection is required');
      if (!formData.amount) validationErrors.push('Donation amount is required');
      if (!formData.name.trim()) validationErrors.push('Name is required');
      if (!formData.email.trim()) {
        validationErrors.push('Email is required');
      } else if (!EMAIL_REGEX.test(formData.email)) {
        validationErrors.push('Invalid email format');
      }
      if (!formData.number.trim()) {
        validationErrors.push('Phone number is required');
      } else if (!PHONE_REGEX.test(formData.number)) {
        validationErrors.push('Invalid phone number format');
      }
      if (!formData.address.trim()) validationErrors.push('Address is required');

      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(' | '));
      }

      // Get current date in YYYY-MM-DD format
      const currentDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });

      const postData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        postData.append(key, value);
      });
      // Add current date to form data
      postData.append('date', currentDate);

      const response = await axios.post(API_PATH, postData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

 
      if (!response.data.sent) {
        throw new Error(response.data.message || 'Form submission failed');
      }

      // Navigate to result page with form data including the date
      navigate('/Donation-Confirmation', { 
        state: { 
          donationData: {
            ...formData,
            date: currentDate
          } 
        } 
      });

    } catch (err) {
      setError(err.response?.data?.message || err.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }

  };

  return (
    <section className="donation">
      <form className="donation-form form" onSubmit={handleSubmitForm}>
        <div className="box-container">
          <div className="item personal-info">
            <h4>Choose the campaign you want to support:</h4>
            <div className="select">
              <div className="select-container">
                <select 
                  className="select-box" 
                  id="campaignType" 
                  name="campaignType"
                  value={formData.campaignType}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                >
                  <option value="">Select Campaign</option>
                  <option value="vehicleUpgrade">Vehicle Upgrade</option>
                  <option value="CPRTraining">Community CPR Training</option>
                  <option value="fireFighterWellness">FireFighter Wellness</option>
                  <option value="safetyDrills">Fire Safety Drills</option>
                  <option value="departmentUpgrade">Fire Department Upgrade</option>
                  <option value="departmentGear">Fire Department Gear</option>
                </select>
                <div className="icon-container">
                  <FaAngleDown />
                </div>
              </div>
            </div>
          </div>

          <div className="item amount-box">
            <h4>Select the amount you want to donate:</h4>
            <div className="amount-input-box">
              <label htmlFor="amount" className="fas fa-dollar-sign"><FaDollarSign /></label>
              <input 
                type="number" 
                id="amount" 
                name="amount"
                placeholder="$10"
                value={formData.amount}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              />
            </div>
            <div className="amount-choices">
              {[10, 30, 50, 75, 100].map((amount) => (
                <div 
                  key={amount}
                  className={`amount-item ${selectedAmount === amount ? 'selected' : ''}`}
                  onClick={() => handleAmountSelection(amount)}
                >
                  ${amount}.00
                </div>
              ))}
            </div>
          </div>

          <div className="item">
            <h4>Select your payment method:</h4>
            <div className="payment-options">
              <div className="payment">
                <input 
                  type="radio" 
                  id="test" 
                  name="payment" 
                  value="test"
                  checked={formData.payment === 'test'}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                />
                <label htmlFor="test" className="radio-label">Test Donation</label>
              </div>
              <div className="payment">
                <input 
                  type="radio" 
                  id="offline" 
                  name="payment" 
                  value="offline"
                  checked={formData.payment === 'offline'}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                />
                <label htmlFor="offline" className="radio-label">Offline donation</label>
              </div>
            </div>
          </div>

          {formData.payment === 'offline' && (
            <div className="offline-payment">
              <h4>Offline Donation Instructions:</h4>
              <OfflineInstructions />

            </div>
          )}
        </div>

        <div className="box-container">
          <div className="item personal-info">
            <h4>Provide your personal information:</h4>
            <div className="input-box">
              <input 
                type="text" 
                className="box" 
                name="name" 
                id="name" 
                placeholder="Name" 
                value={formData.name}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              />
              <input 
                type="email" 
                className="box" 
                name="email" 
                id="email" 
                placeholder="Email" 
                value={formData.email}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              />
            </div>
            <div className="input-box">
              <input 
                type="tel" 
                className="box" 
                name="number" 
                id="number" 
                placeholder="Phone Number" 
                value={formData.number}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              />
              <input 
                type="text" 
                className="box" 
                name="address" 
                id="address" 
                placeholder="Address" 
                value={formData.address}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              />
            </div>
            <textarea 
              className="box" 
              rows="10" 
              cols="30" 
              name="comment" 
              id="comment" 
              placeholder="Your message" 
              value={formData.comment}
              onChange={handleInputChange}
              disabled={isSubmitting}
            ></textarea>
          </div>

          <div className="total-donation">
            <h4>Total donation:</h4>
            <h6>${formData.amount || '0'}</h6>
          </div>

          <button type="submit" className="btn" name="donate" id="donate" disabled={isSubmitting}>
            {isSubmitting ? 'Processing...' : 'Donate Now'}
          </button>

          {error && (
            <div className="error-message">
              {error.split(' | ').map((msg, i) => (
                <p key={i}>{msg}</p>
              ))}
            </div>
          )}
        </div>
      </form>
    </section>
  );
};

export default Donation;